import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
  Pagination,
  InputAdornment,
  Collapse,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  VisibilityOff as HideIcon,
  ContentCopy as CopyIcon,
  Refresh as RefreshIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Api as ApiIcon,
  Storage as StorageIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import apiService from '../services/api';
import { authService } from '../services/auth-service';
import { Partner, ApiKey, SftpSshKey, CreatePartnerRequest, CreateApiKeyRequest, CreateSftpSshKeyRequest } from '../types/Partner';
import { convertPkcs8ToPkcs1, convertPkcs8ToOpenSSH, getKeyFormat } from '../utils/keyConversion';

const PartnerIntegration: React.FC = () => {
  // State management
  const [partners, setPartners] = useState<Partner[]>([]);
  const [partnerApiKeys, setPartnerApiKeys] = useState<Record<string, ApiKey[]>>({});
  const [partnerSftpSshKeys, setPartnerSftpSshKeys] = useState<Record<string, SftpSshKey[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Dialog states
  const [partnerDialogOpen, setPartnerDialogOpen] = useState(false);
  const [apiKeyDialogOpen, setApiKeyDialogOpen] = useState(false);
  const [sftpSshKeyDialogOpen, setSftpSshKeyDialogOpen] = useState(false);
  const [viewApiKeyDialogOpen, setViewApiKeyDialogOpen] = useState(false);
  const [viewSftpSshKeyDialogOpen, setViewSftpSshKeyDialogOpen] = useState(false);
  const [editingPartner, setEditingPartner] = useState<Partner | null>(null);
  const [editingApiKey, setEditingApiKey] = useState<ApiKey | null>(null);
  const [editingSftpSshKey, setEditingSftpSshKey] = useState<SftpSshKey | null>(null);
  const [viewingApiKey, setViewingApiKey] = useState<ApiKey | null>(null);
  const [viewingSftpSshKey, setViewingSftpSshKey] = useState<SftpSshKey | null>(null);
  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null);
  
  // Form states
  const [partnerForm, setPartnerForm] = useState<CreatePartnerRequest>({
    name: '',
    organizationType: 'medical_office',
    contactEmail: '',
    contactPhone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    website: '',
    description: '',
  });
  
  const [apiKeyForm, setApiKeyForm] = useState<CreateApiKeyRequest>({
    partnerId: '',
    name: '',
    permissions: ['lab_orders:read', 'lab_orders:create'],
    rateLimit: {
      requestsPerMinute: 100,
      requestsPerHour: 1000,
    },
  });
  
  const [sftpSshKeyForm, setSftpSshKeyForm] = useState<CreateSftpSshKeyRequest>({
    partnerId: '',
    name: '',
    sftpServer: '34.60.157.137',
    sftpPort: 22,
  });
  
  // UI states
  const [showApiKey, setShowApiKey] = useState(false);
  const [showSftpSshKey, setShowSftpSshKey] = useState(false);
  const [expandedPartner, setExpandedPartner] = useState<string | null>(null);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordVerifying, setPasswordVerifying] = useState(false);
  const [pendingApiKeyAction, setPendingApiKeyAction] = useState<(() => void) | null>(null);
  const [pendingSftpSshKeyAction, setPendingSftpSshKeyAction] = useState<(() => void) | null>(null);
  const [apiKeyViewTimeout, setApiKeyViewTimeout] = useState<NodeJS.Timeout | null>(null);
  const [sftpSshKeyViewTimeout, setSftpSshKeyViewTimeout] = useState<NodeJS.Timeout | null>(null);
  
  // Download format dialog state
  const [downloadFormatDialogOpen, setDownloadFormatDialogOpen] = useState(false);
  const [pendingDownloadKey, setPendingDownloadKey] = useState<SftpSshKey | null>(null);

  // Load API keys for a specific partner
  const loadApiKeysForPartner = async (partnerId: string) => {
    try {
      const response = await apiService.getApiKeys(partnerId);
      const apiKeys = response.apiKeys || [];
      
      // Parse permissions from JSON string to array for each API key
      const parsedApiKeys = apiKeys.map((apiKey: any) => ({
        ...apiKey,
        permissions: typeof apiKey.permissions === 'string' 
          ? JSON.parse(apiKey.permissions) 
          : apiKey.permissions,
        rateLimit: {
          requestsPerMinute: apiKey.rateLimitRequestsPerMinute,
          requestsPerHour: apiKey.rateLimitRequestsPerHour,
        }
      }));
      
      setPartnerApiKeys(prev => ({
        ...prev,
        [partnerId]: parsedApiKeys
      }));
    } catch (err) {
      console.error('Error loading API keys for partner:', partnerId, err);
    }
  };

  // Load SFTP SSH keys for a specific partner
  const loadSftpSshKeysForPartner = async (partnerId: string) => {
    try {
      const response = await apiService.getSftpSshKeys(partnerId);
      // The API returns the array directly, not wrapped in an sftpSshKeys property
      const sftpSshKeys = Array.isArray(response) ? response : [];
      
      setPartnerSftpSshKeys(prev => ({
        ...prev,
        [partnerId]: sftpSshKeys
      }));
    } catch (err) {
      console.error('Error loading SFTP SSH keys for partner:', partnerId, err);
    }
  };

  // Load partners
  const loadPartners = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getPartners(page, pageSize, searchTerm);
      const partnersData = response.partners || [];
      setPartners(partnersData);
      setTotal(response.total || 0);
      
      // Load API keys for each partner
      const apiKeysPromises = partnersData.map((partner: Partner) => loadApiKeysForPartner(partner.id));
      await Promise.all(apiKeysPromises);

      // Load SFTP SSH keys for each partner
      const sftpSshKeysPromises = partnersData.map((partner: Partner) => loadSftpSshKeysForPartner(partner.id));
      await Promise.all(sftpSshKeysPromises);
    } catch (err) {
      setError('Failed to load partners');
      console.error('Error loading partners:', err);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchTerm]);

  useEffect(() => {
    loadPartners();
  }, [loadPartners]);

  // Cleanup timeout when component unmounts
  useEffect(() => {
    return () => {
      if (apiKeyViewTimeout) {
        clearTimeout(apiKeyViewTimeout);
      }
      if (sftpSshKeyViewTimeout) {
        clearTimeout(sftpSshKeyViewTimeout);
      }
    };
  }, [apiKeyViewTimeout, sftpSshKeyViewTimeout]);



  // Handle partner form changes
  const handlePartnerFormChange = (field: keyof CreatePartnerRequest, value: any) => {
    setPartnerForm(prev => ({ ...prev, [field]: value }));
  };

  // Handle API key form changes
  const handleApiKeyFormChange = (field: keyof CreateApiKeyRequest, value: any) => {
    setApiKeyForm(prev => ({ ...prev, [field]: value }));
  };

  // Handle SFTP SSH key form changes
  const handleSftpSshKeyFormChange = (field: keyof CreateSftpSshKeyRequest, value: any) => {
    setSftpSshKeyForm(prev => ({ ...prev, [field]: value }));
  };

  // Open partner dialog
  const handleOpenPartnerDialog = (partner?: Partner) => {
    if (partner) {
      setEditingPartner(partner);
      setPartnerForm({
        name: partner.name,
        organizationType: partner.organizationType,
        contactEmail: partner.contactEmail,
        contactPhone: partner.contactPhone || '',
        address: partner.address || '',
        city: partner.city || '',
        state: partner.state || '',
        zipCode: partner.zipCode || '',
        country: partner.country || '',
        website: partner.website || '',
        description: partner.description || '',
      });
    } else {
      setEditingPartner(null);
      setPartnerForm({
        name: '',
        organizationType: 'medical_office',
        contactEmail: '',
        contactPhone: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        country: '',
        website: '',
        description: '',
      });
    }
    setPartnerDialogOpen(true);
  };

  // Save partner
  const handleSavePartner = async () => {
    try {
      if (editingPartner) {
        await apiService.updatePartner(editingPartner.id, partnerForm);
        setSuccessMessage('Partner updated successfully');
      } else {
        await apiService.createPartner(partnerForm);
        setSuccessMessage('Partner created successfully');
      }
      setPartnerDialogOpen(false);
      loadPartners();
    } catch (err) {
      setError('Failed to save partner');
      console.error('Error saving partner:', err);
    }
  };

  // Delete partner
  const handleDeletePartner = async (partnerId: string) => {
    if (window.confirm('Are you sure you want to delete this partner? This will also delete all associated API keys.')) {
      try {
        await apiService.deletePartner(partnerId);
        setSuccessMessage('Partner deleted successfully');
        loadPartners();
      } catch (err) {
        setError('Failed to delete partner');
        console.error('Error deleting partner:', err);
      }
    }
  };

  // Open API key dialog
  const handleOpenApiKeyDialog = (partner: Partner, apiKey?: ApiKey) => {
    setSelectedPartner(partner);
    if (apiKey) {
      setEditingApiKey(apiKey);
      setApiKeyForm({
        partnerId: partner.id,
        name: apiKey.name,
        permissions: apiKey.permissions,
        rateLimit: apiKey.rateLimit || {
          requestsPerMinute: 100,
          requestsPerHour: 1000,
        },
        expiresAt: apiKey.expiresAt,
      });
    } else {
      setEditingApiKey(null);
      setApiKeyForm({
        partnerId: partner.id,
        name: '',
        permissions: ['lab_orders:read', 'lab_orders:create'],
        rateLimit: {
          requestsPerMinute: 100,
          requestsPerHour: 1000,
        },
      });
    }
    setApiKeyDialogOpen(true);
  };

  // Verify password for viewing sensitive data
  const handleVerifyPassword = async () => {
    try {
      setPasswordVerifying(true);
      setPasswordError(null);
      
      // Verify password with auth service
      await authService.reauthenticateWithPassword(password);
      setPasswordDialogOpen(false);
      setPassword('');
      setPasswordVerifying(false);
      
      // Execute the pending action if it exists
      if (pendingApiKeyAction) {
        pendingApiKeyAction();
        setPendingApiKeyAction(null);
        
        // Set a timeout to automatically hide the API key after 5 minutes
        const timeout = setTimeout(() => {
          setShowApiKey(false);
        }, 5 * 60 * 1000); // 5 minutes
        
        setApiKeyViewTimeout(timeout);
      }

      if (pendingSftpSshKeyAction) {
        pendingSftpSshKeyAction();
        setPendingSftpSshKeyAction(null);
        
        // Set a timeout to automatically hide the SFTP SSH key after 5 minutes
        const timeout = setTimeout(() => {
          setShowSftpSshKey(false);
        }, 5 * 60 * 1000); // 5 minutes
        
        setSftpSshKeyViewTimeout(timeout);
      }
    } catch (err: any) {
      console.error('Password verification error:', err);
      setPasswordVerifying(false);
      setPasswordError(err.message || 'Invalid password');
      
      // Don't close the dialog on error, let the user try again
      // setPasswordDialogOpen(false);
    }
  };

  // Save API key
  const handleSaveApiKey = async () => {
    try {
      if (editingApiKey) {
        await apiService.updateApiKey(editingApiKey.id, apiKeyForm);
        setSuccessMessage('API key updated successfully');
      } else {
        await apiService.createApiKey(apiKeyForm);
        setSuccessMessage('API key created successfully');
        
        // Refresh API keys for the specific partner to get the newly created one
        if (selectedPartner) {
          await loadApiKeysForPartner(selectedPartner.id);
        }
      }
      setApiKeyDialogOpen(false);
      // Refresh API keys for the specific partner
      if (selectedPartner) {
        await loadApiKeysForPartner(selectedPartner.id);
      }
    } catch (err) {
      setError('Failed to save API key');
      console.error('Error saving API key:', err);
    }
  };

  // Delete API key
  const handleDeleteApiKey = async (apiKeyId: string) => {
    if (window.confirm('Are you sure you want to delete this API key?')) {
      try {
        await apiService.deleteApiKey(apiKeyId);
        setSuccessMessage('API key deleted successfully');
        // Refresh API keys for all partners to update the count
        const apiKeysPromises = partners.map((partner: Partner) => loadApiKeysForPartner(partner.id));
        await Promise.all(apiKeysPromises);
      } catch (err) {
        setError('Failed to delete API key');
        console.error('Error deleting API key:', err);
      }
    }
  };

  // Regenerate API key
  const handleRegenerateApiKey = async (apiKeyId: string) => {
    if (window.confirm('Are you sure you want to regenerate this API key? The old key will be invalidated.')) {
      try {
        await apiService.regenerateApiKey(apiKeyId);
        setSuccessMessage('API key regenerated successfully');
        // Refresh API keys for all partners to update the data
        const apiKeysPromises = partners.map((partner: Partner) => loadApiKeysForPartner(partner.id));
        await Promise.all(apiKeysPromises);
      } catch (err) {
        setError('Failed to regenerate API key');
        console.error('Error regenerating API key:', err);
      }
    }
  };

  // View API key
  const handleViewApiKey = (apiKey: ApiKey) => {
    setViewingApiKey(apiKey);
    setPendingApiKeyAction(() => () => {
      setViewApiKeyDialogOpen(true);
    });
    setPasswordDialogOpen(true);
  };

  // Copy API key to clipboard
  const handleCopyApiKey = (apiKey: string) => {
    navigator.clipboard.writeText(apiKey);
    setSuccessMessage('API key copied to clipboard');
  };

  // Toggle API key visibility with timeout reset
  const handleToggleApiKeyVisibility = () => {
    setShowApiKey(!showApiKey);
    
    // Reset the timeout when toggling visibility
    if (apiKeyViewTimeout) {
      clearTimeout(apiKeyViewTimeout);
    }
    
    if (!showApiKey) {
      // API key is being shown, set new timeout
      const timeout = setTimeout(() => {
        setShowApiKey(false);
      }, 5 * 60 * 1000); // 5 minutes
      
      setApiKeyViewTimeout(timeout);
    } else {
      // API key is being hidden, clear timeout
      setApiKeyViewTimeout(null);
    }
  };

  // Open SFTP SSH key dialog
  const handleOpenSftpSshKeyDialog = (partner: Partner, sftpSshKey?: SftpSshKey) => {
    setSelectedPartner(partner);
    if (sftpSshKey) {
      setEditingSftpSshKey(sftpSshKey);
      setSftpSshKeyForm({
        partnerId: partner.id,
        name: sftpSshKey.name,
        sftpServer: sftpSshKey.sftpServer,
        sftpPort: sftpSshKey.sftpPort,
        expiresAt: sftpSshKey.expiresAt,
      });
    } else {
      setEditingSftpSshKey(null);
      setSftpSshKeyForm({
        partnerId: partner.id,
        name: '',
        sftpServer: '34.60.157.137',
        sftpPort: 22,
      });
    }
    setSftpSshKeyDialogOpen(true);
  };

  // Save SFTP SSH key
  const handleSaveSftpSshKey = async () => {
    try {
      if (editingSftpSshKey) {
        await apiService.updateSftpSshKey(editingSftpSshKey.id, sftpSshKeyForm);
        setSuccessMessage('SFTP SSH key updated successfully');
      } else {
        await apiService.createSftpSshKey(sftpSshKeyForm);
        setSuccessMessage('SFTP SSH key created successfully');
        
        // Refresh SFTP SSH keys for the specific partner to get the newly created one
        if (selectedPartner) {
          await loadSftpSshKeysForPartner(selectedPartner.id);
        }
      }
      setSftpSshKeyDialogOpen(false);
      // Refresh SFTP SSH keys for the specific partner
      if (selectedPartner) {
        await loadSftpSshKeysForPartner(selectedPartner.id);
      }
    } catch (err) {
      setError('Failed to save SFTP SSH key');
      console.error('Error saving SFTP SSH key:', err);
    }
  };

  // Delete SFTP SSH key
  const handleDeleteSftpSshKey = async (sftpSshKeyId: string) => {
    if (window.confirm('Are you sure you want to delete this SFTP SSH key?')) {
      try {
        await apiService.deleteSftpSshKey(sftpSshKeyId);
        setSuccessMessage('SFTP SSH key deleted successfully');
        // Refresh SFTP SSH keys for all partners to update the count
        const sftpSshKeysPromises = partners.map((partner: Partner) => loadSftpSshKeysForPartner(partner.id));
        await Promise.all(sftpSshKeysPromises);
      } catch (err) {
        setError('Failed to delete SFTP SSH key');
        console.error('Error deleting SFTP SSH key:', err);
      }
    }
  };

  // View SFTP SSH key
  const handleViewSftpSshKey = (sftpSshKey: SftpSshKey) => {
    setViewingSftpSshKey(sftpSshKey);
    setPendingSftpSshKeyAction(() => () => {
      setViewSftpSshKeyDialogOpen(true);
    });
    setPasswordDialogOpen(true);
  };

  // Copy SFTP SSH key to clipboard
  const handleCopySftpSshKey = (sftpSshKey: string) => {
    navigator.clipboard.writeText(sftpSshKey);
    setSuccessMessage('SFTP SSH key copied to clipboard');
  };

  // Show download format selection dialog
  const handleDownloadSftpSshKey = (sftpSshKey: SftpSshKey) => {
    if (!showSftpSshKey) {
      // If key is not visible, prompt user to show it first
      setError('Please show the private key before downloading');
      return;
    }

    setPendingDownloadKey(sftpSshKey);
    setDownloadFormatDialogOpen(true);
  };

  // Download SFTP SSH private key in specified format
  const handleDownloadKeyInFormat = (format: 'pkcs8' | 'pkcs1' | 'openssh') => {
    if (!pendingDownloadKey) return;

    try {
      let keyContent = pendingDownloadKey.privateKey;
      let fileExtension = 'pem';
      let formatDescription = '';
      let formatLabel = '';

      const currentFormat = getKeyFormat(pendingDownloadKey.privateKey);

      if (format === 'pkcs1') {
        // Convert to PKCS#1 if needed
        if (currentFormat === 'pkcs8') {
          keyContent = convertPkcs8ToPkcs1(pendingDownloadKey.privateKey);
        } else if (currentFormat === 'openssh') {
          // Convert OpenSSH to PKCS#8 first, then to PKCS#1
          const pkcs8Key = pendingDownloadKey.privateKey
            .replace('-----BEGIN OPENSSH PRIVATE KEY-----', '-----BEGIN PRIVATE KEY-----')
            .replace('-----END OPENSSH PRIVATE KEY-----', '-----END PRIVATE KEY-----');
          keyContent = convertPkcs8ToPkcs1(pkcs8Key);
        }
        formatDescription = ' (PKCS#1)';
        formatLabel = 'PKCS#1';
      } else if (format === 'openssh') {
        // Convert to OpenSSH if needed
        if (currentFormat === 'pkcs8') {
          keyContent = convertPkcs8ToOpenSSH(pendingDownloadKey.privateKey);
        } else if (currentFormat === 'pkcs1') {
          // Convert PKCS#1 to PKCS#8 first, then to OpenSSH
          const pkcs8Key = convertPkcs8ToPkcs1(pendingDownloadKey.privateKey);
          keyContent = convertPkcs8ToOpenSSH(pkcs8Key);
        }
        formatDescription = ' (OpenSSH)';
        formatLabel = 'OpenSSH';
      } else {
        // PKCS#8 format (default)
        formatDescription = ' (PKCS#8)';
        formatLabel = 'PKCS#8';
      }

      // Create a blob with the private key content
      const blob = new Blob([keyContent], { type: 'text/plain' });
      
      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Set filename based on partner name and key name
      const sanitizedName = pendingDownloadKey.name.toLowerCase().replace(/[^a-z0-9]/g, '-');
      link.download = `${pendingDownloadKey.username}-${sanitizedName}-private-${format}.${fileExtension}`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setSuccessMessage(`Private key file downloaded successfully${formatDescription}`);
      setDownloadFormatDialogOpen(false);
      setPendingDownloadKey(null);
    } catch (err) {
      setError('Failed to download private key file');
      console.error('Error downloading private key:', err);
    }
  };

  // Toggle SFTP SSH key visibility with timeout reset
  const handleToggleSftpSshKeyVisibility = () => {
    setShowSftpSshKey(!showSftpSshKey);
    
    // Reset the timeout when toggling visibility
    if (sftpSshKeyViewTimeout) {
      clearTimeout(sftpSshKeyViewTimeout);
    }
    
    if (!showSftpSshKey) {
      // SFTP SSH key is being shown, set new timeout
      const timeout = setTimeout(() => {
        setShowSftpSshKey(false);
      }, 5 * 60 * 1000); // 5 minutes
      
      setSftpSshKeyViewTimeout(timeout);
    } else {
      // SFTP SSH key is being hidden, clear timeout
      setSftpSshKeyViewTimeout(null);
    }
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'error';
      case 'pending': return 'warning';
      case 'expired': return 'error';
      default: return 'default';
    }
  };

  // Get organization type label
  const getOrganizationTypeLabel = (type: string) => {
    switch (type) {
      case 'medical_office': return 'Medical Office';
      case 'hospital': return 'Hospital';
      case 'research_company': return 'Research Company';
      case 'other': return 'Other';
      default: return type;
    }
  };

  // Permission options
  const permissionOptions = [
    { value: 'lab_orders:read', label: 'Read Lab Orders' },
    { value: 'lab_orders:create', label: 'Create Lab Orders' },
    { value: 'lab_orders:update', label: 'Update Lab Orders' },
    { value: 'patients:read', label: 'Read Patients' },
    { value: 'patients:create', label: 'Create Patients' },
    { value: 'reports:read', label: 'Read Reports' },
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Partner Integration
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenPartnerDialog()}
        >
          Add Partner
        </Button>
      </Box>

      {/* Search */}
      <Box mb={3}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search partners..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <BusinessIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Partners Table */}
      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'grey.50' }}>
              <TableCell width="30px"></TableCell>
              <TableCell><strong>Partner</strong></TableCell>
              <TableCell><strong>Type</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Contact</strong></TableCell>
              <TableCell><strong>API Keys</strong></TableCell>
              <TableCell><strong>SFTP Keys</strong></TableCell>
              <TableCell width="120px"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {partners.map((partner) => {
              const isExpanded = expandedPartner === partner.id;
              const apiKeys = partnerApiKeys[partner.id] || [];
              const sftpKeys = partnerSftpSshKeys[partner.id] || [];
              
              return (
                <React.Fragment key={partner.id}>
                  {/* Main Partner Row */}
                  <TableRow 
                    hover 
                    sx={{ 
                      '&:hover': { backgroundColor: 'grey.50' },
                      cursor: 'pointer'
                    }}
                    onClick={() => setExpandedPartner(isExpanded ? null : partner.id)}
                  >
                    <TableCell>
                      <IconButton size="small">
                        {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {partner.name}
                        </Typography>
                        {partner.description && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {partner.description.length > 50 
                              ? `${partner.description.substring(0, 50)}...` 
                              : partner.description}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getOrganizationTypeLabel(partner.organizationType)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={partner.status}
                        color={getStatusColor(partner.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" display="flex" alignItems="center" gap={0.5}>
                          <EmailIcon sx={{ fontSize: 14 }} />
                          {partner.contactEmail}
                        </Typography>
                        {partner.contactPhone && (
                          <Typography variant="body2" display="flex" alignItems="center" gap={0.5}>
                            <PhoneIcon sx={{ fontSize: 14 }} />
                            {partner.contactPhone}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <ApiIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                        <Typography variant="body2">
                          {apiKeys.length} key{apiKeys.length !== 1 ? 's' : ''}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <StorageIcon sx={{ fontSize: 16, color: 'secondary.main' }} />
                        <Typography variant="body2">
                          {sftpKeys.length} key{sftpKeys.length !== 1 ? 's' : ''}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={0.5}>
                        <Tooltip title="Edit Partner">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenPartnerDialog(partner);
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Partner">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeletePartner(partner.id);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>

                  {/* Expanded Details Row */}
                  <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 2, px: 1 }}>
                          <Grid container spacing={3}>
                            {/* API Keys Section */}
                            <Grid item xs={12} md={6}>
                              <Card variant="outlined">
                                <CardContent sx={{ pb: 1 }}>
                                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                    <Typography variant="h6" component="h3" display="flex" alignItems="center" gap={1}>
                                      <ApiIcon color="primary" />
                                      API Keys ({apiKeys.length})
                                    </Typography>
                                    <Button
                                      size="small"
                                      startIcon={<AddIcon />}
                                      onClick={() => handleOpenApiKeyDialog(partner)}
                                    >
                                      Add
                                    </Button>
                                  </Box>

                                  {apiKeys.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                                      No API keys configured
                                    </Typography>
                                  ) : (
                                    <List dense sx={{ py: 0 }}>
                                      {apiKeys.map((apiKey) => (
                                        <ListItem key={apiKey.id} sx={{ px: 0, py: 0.5 }}>
                                          <ListItemText
                                            primary={
                                              <Box display="flex" alignItems="center" gap={1}>
                                                <Typography variant="body2" fontWeight="medium">
                                                  {apiKey.name}
                                                </Typography>
                                                <Chip
                                                  label={apiKey.status}
                                                  color={getStatusColor(apiKey.status) as any}
                                                  size="small"
                                                />
                                              </Box>
                                            }
                                            secondary={
                                              apiKey.lastUsed && (
                                                <Typography variant="caption" color="text.secondary">
                                                  Last used: {new Date(apiKey.lastUsed).toLocaleDateString()}
                                                </Typography>
                                              )
                                            }
                                          />
                                          <ListItemSecondaryAction>
                                            <Box display="flex" gap={0.5}>
                                              <Tooltip title="View Key">
                                                <IconButton
                                                  size="small"
                                                  onClick={() => handleViewApiKey(apiKey)}
                                                >
                                                  <ViewIcon fontSize="small" />
                                                </IconButton>
                                              </Tooltip>
                                              <Tooltip title="Edit">
                                                <IconButton
                                                  size="small"
                                                  onClick={() => handleOpenApiKeyDialog(partner, apiKey)}
                                                >
                                                  <EditIcon fontSize="small" />
                                                </IconButton>
                                              </Tooltip>
                                              <Tooltip title="Regenerate">
                                                <IconButton
                                                  size="small"
                                                  onClick={() => handleRegenerateApiKey(apiKey.id)}
                                                >
                                                  <RefreshIcon fontSize="small" />
                                                </IconButton>
                                              </Tooltip>
                                              <Tooltip title="Delete">
                                                <IconButton
                                                  size="small"
                                                  color="error"
                                                  onClick={() => handleDeleteApiKey(apiKey.id)}
                                                >
                                                  <DeleteIcon fontSize="small" />
                                                </IconButton>
                                              </Tooltip>
                                            </Box>
                                          </ListItemSecondaryAction>
                                        </ListItem>
                                      ))}
                                    </List>
                                  )}
                                </CardContent>
                              </Card>
                            </Grid>

                            {/* SFTP SSH Keys Section */}
                            <Grid item xs={12} md={6}>
                              <Card variant="outlined">
                                <CardContent sx={{ pb: 1 }}>
                                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                    <Typography variant="h6" component="h3" display="flex" alignItems="center" gap={1}>
                                      <StorageIcon color="secondary" />
                                      SFTP SSH Keys ({sftpKeys.length})
                                    </Typography>
                                    <Button
                                      size="small"
                                      startIcon={<AddIcon />}
                                      onClick={() => handleOpenSftpSshKeyDialog(partner)}
                                    >
                                      Add
                                    </Button>
                                  </Box>

                                  {sftpKeys.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                                      No SFTP SSH keys configured
                                    </Typography>
                                  ) : (
                                    <List dense sx={{ py: 0 }}>
                                      {sftpKeys.map((sftpKey) => (
                                        <ListItem key={sftpKey.id} sx={{ px: 0, py: 0.5 }}>
                                          <ListItemText
                                            primary={
                                              <Box display="flex" alignItems="center" gap={1}>
                                                <Typography variant="body2" fontWeight="medium">
                                                  {sftpKey.name}
                                                </Typography>
                                                <Chip
                                                  label={sftpKey.status}
                                                  color={getStatusColor(sftpKey.status) as any}
                                                  size="small"
                                                />
                                              </Box>
                                            }
                                            secondary={
                                              <Box>
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                  {sftpKey.username}@{sftpKey.sftpServer}:{sftpKey.sftpPort}
                                                </Typography>
                                                {sftpKey.lastUsed && (
                                                  <Typography variant="caption" color="text.secondary">
                                                    Last used: {new Date(sftpKey.lastUsed).toLocaleDateString()}
                                                  </Typography>
                                                )}
                                              </Box>
                                            }
                                          />
                                          <ListItemSecondaryAction>
                                            <Box display="flex" gap={0.5}>
                                              <Tooltip title="View Key">
                                                <IconButton
                                                  size="small"
                                                  onClick={() => handleViewSftpSshKey(sftpKey)}
                                                >
                                                  <ViewIcon fontSize="small" />
                                                </IconButton>
                                              </Tooltip>
                                              <Tooltip title="Edit">
                                                <IconButton
                                                  size="small"
                                                  onClick={() => handleOpenSftpSshKeyDialog(partner, sftpKey)}
                                                >
                                                  <EditIcon fontSize="small" />
                                                </IconButton>
                                              </Tooltip>
                                              <Tooltip title="Delete">
                                                <IconButton
                                                  size="small"
                                                  color="error"
                                                  onClick={() => handleDeleteSftpSshKey(sftpKey.id)}
                                                >
                                                  <DeleteIcon fontSize="small" />
                                                </IconButton>
                                              </Tooltip>
                                            </Box>
                                          </ListItemSecondaryAction>
                                        </ListItem>
                                      ))}
                                    </List>
                                  )}
                                </CardContent>
                              </Card>
                            </Grid>
                          </Grid>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {total > pageSize && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={Math.ceil(total / pageSize)}
            page={page}
            onChange={(_, newPage) => setPage(newPage)}
            color="primary"
          />
        </Box>
      )}

      {/* Partner Dialog */}
      <Dialog open={partnerDialogOpen} onClose={() => setPartnerDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingPartner ? 'Edit Partner' : 'Add New Partner'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Organization Name"
                value={partnerForm.name}
                onChange={(e) => handlePartnerFormChange('name', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Organization Type</InputLabel>
                <Select
                  value={partnerForm.organizationType}
                  onChange={(e) => handlePartnerFormChange('organizationType', e.target.value)}
                  label="Organization Type"
                >
                  <MenuItem value="medical_office">Medical Office</MenuItem>
                  <MenuItem value="hospital">Hospital</MenuItem>
                  <MenuItem value="research_company">Research Company</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact Email"
                type="email"
                value={partnerForm.contactEmail}
                onChange={(e) => handlePartnerFormChange('contactEmail', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact Phone"
                value={partnerForm.contactPhone}
                onChange={(e) => handlePartnerFormChange('contactPhone', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                value={partnerForm.address}
                onChange={(e) => handlePartnerFormChange('address', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="City"
                value={partnerForm.city}
                onChange={(e) => handlePartnerFormChange('city', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="State"
                value={partnerForm.state}
                onChange={(e) => handlePartnerFormChange('state', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="ZIP Code"
                value={partnerForm.zipCode}
                onChange={(e) => handlePartnerFormChange('zipCode', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Country"
                value={partnerForm.country}
                onChange={(e) => handlePartnerFormChange('country', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Website"
                value={partnerForm.website}
                onChange={(e) => handlePartnerFormChange('website', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={partnerForm.description}
                onChange={(e) => handlePartnerFormChange('description', e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPartnerDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSavePartner} variant="contained">
            {editingPartner ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* API Key Dialog */}
      <Dialog open={apiKeyDialogOpen} onClose={() => setApiKeyDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingApiKey ? 'Edit API Key' : 'Create New API Key'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="API Key Name"
                value={apiKeyForm.name}
                onChange={(e) => handleApiKeyFormChange('name', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Permissions</InputLabel>
                <Select
                  multiple
                  value={apiKeyForm.permissions}
                  onChange={(e) => handleApiKeyFormChange('permissions', e.target.value)}
                  label="Permissions"
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={permissionOptions.find(p => p.value === value)?.label || value} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {permissionOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Rate Limit (requests per minute)"
                type="number"
                value={apiKeyForm.rateLimit?.requestsPerMinute}
                onChange={(e) => handleApiKeyFormChange('rateLimit', {
                  ...apiKeyForm.rateLimit,
                  requestsPerMinute: parseInt(e.target.value) || 100
                })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Rate Limit (requests per hour)"
                type="number"
                value={apiKeyForm.rateLimit?.requestsPerHour}
                onChange={(e) => handleApiKeyFormChange('rateLimit', {
                  ...apiKeyForm.rateLimit,
                  requestsPerHour: parseInt(e.target.value) || 1000
                })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Expiration Date (optional)"
                type="datetime-local"
                value={apiKeyForm.expiresAt ? apiKeyForm.expiresAt.slice(0, 16) : ''}
                onChange={(e) => handleApiKeyFormChange('expiresAt', e.target.value ? new Date(e.target.value).toISOString() : undefined)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApiKeyDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveApiKey} variant="contained">
            {editingApiKey ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password Verification Dialog */}
      <Dialog 
        open={passwordDialogOpen} 
        onClose={() => {
          if (!passwordVerifying) {
            setPasswordDialogOpen(false);
            setPassword('');
            setPasswordError(null);
            setPendingApiKeyAction(null);
            setPendingSftpSshKeyAction(null);
          }
        }} 
        maxWidth="sm" 
        fullWidth
        keepMounted={false}
        disableEscapeKeyDown={passwordVerifying}
      >
        <DialogTitle>Enter Your Password</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please enter your password to view the sensitive data.
          </Typography>
          <TextField
            fullWidth
            type="password"
            label="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={!!passwordError}
            helperText={passwordError}
            disabled={passwordVerifying}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !passwordVerifying) {
                handleVerifyPassword();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setPasswordDialogOpen(false);
              setPassword('');
              setPasswordError(null);
              setPendingApiKeyAction(null);
              setPendingSftpSshKeyAction(null);
            }} 
            disabled={passwordVerifying}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleVerifyPassword} 
            variant="contained"
            disabled={passwordVerifying || !password.trim()}
            startIcon={passwordVerifying ? <CircularProgress size={20} /> : undefined}
          >
            {passwordVerifying ? 'Verifying...' : 'Verify'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View API Key Dialog */}
      <Dialog 
        open={viewApiKeyDialogOpen} 
        onClose={() => {
          setViewApiKeyDialogOpen(false);
          setShowApiKey(false);
          if (apiKeyViewTimeout) {
            clearTimeout(apiKeyViewTimeout);
            setApiKeyViewTimeout(null);
          }
        }} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>API Key Details</DialogTitle>
        <DialogContent>
          {viewingApiKey && (
            <Box>
              <Typography variant="h6" gutterBottom>{viewingApiKey.name}</Typography>
              <Box mb={2}>
                <Chip
                  label={viewingApiKey.status}
                  color={getStatusColor(viewingApiKey.status) as any}
                  sx={{ mr: 1 }}
                />
                {viewingApiKey.expiresAt && (
                  <Chip
                    label={`Expires: ${new Date(viewingApiKey.expiresAt).toLocaleDateString()}`}
                    variant="outlined"
                  />
                )}
              </Box>
              
              <Typography variant="subtitle2" gutterBottom>API Key:</Typography>
              {showApiKey && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="caption">
                    This API key will be automatically hidden in 5 minutes for security.
                  </Typography>
                </Alert>
              )}
              <Box display="flex" alignItems="center" mb={2}>
                <TextField
                  fullWidth
                  value={showApiKey ? viewingApiKey.key : '••••••••••••••••••••••••••••••••'}
                  variant="outlined"
                  size="small"
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleToggleApiKeyVisibility}
                          edge="end"
                        >
                          {showApiKey ? <HideIcon /> : <ViewIcon />}
                        </IconButton>
                        <IconButton
                          onClick={() => handleCopyApiKey(viewingApiKey.key)}
                          edge="end"
                        >
                          <CopyIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Typography variant="subtitle2" gutterBottom>Permissions:</Typography>
              <Box mb={2}>
                {viewingApiKey.permissions.map((permission) => (
                  <Chip
                    key={permission}
                    label={permissionOptions.find(p => p.value === permission)?.label || permission}
                    size="small"
                    sx={{ mr: 1, mb: 1 }}
                  />
                ))}
              </Box>

              {viewingApiKey.rateLimit && (
                <>
                  <Typography variant="subtitle2" gutterBottom>Rate Limits:</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {viewingApiKey.rateLimit.requestsPerMinute} requests per minute, {viewingApiKey.rateLimit.requestsPerHour} requests per hour
                  </Typography>
                </>
              )}

              {viewingApiKey.lastUsed && (
                <>
                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>Last Used:</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {new Date(viewingApiKey.lastUsed).toLocaleString()}
                  </Typography>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewApiKeyDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* SFTP SSH Key Dialog */}
      <Dialog open={sftpSshKeyDialogOpen} onClose={() => setSftpSshKeyDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingSftpSshKey ? 'Edit SFTP SSH Key' : 'Create New SFTP SSH Key'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="SFTP Key Name"
                value={sftpSshKeyForm.name}
                onChange={(e) => handleSftpSshKeyFormChange('name', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="SFTP Server"
                value={sftpSshKeyForm.sftpServer}
                onChange={(e) => handleSftpSshKeyFormChange('sftpServer', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="SFTP Port"
                type="number"
                value={sftpSshKeyForm.sftpPort}
                onChange={(e) => handleSftpSshKeyFormChange('sftpPort', parseInt(e.target.value) || 22)}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Expiration Date (optional)"
                type="datetime-local"
                value={sftpSshKeyForm.expiresAt ? sftpSshKeyForm.expiresAt.slice(0, 16) : ''}
                onChange={(e) => handleSftpSshKeyFormChange('expiresAt', e.target.value ? new Date(e.target.value).toISOString() : undefined)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSftpSshKeyDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveSftpSshKey} variant="contained">
            {editingSftpSshKey ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View SFTP SSH Key Dialog */}
      <Dialog 
        open={viewSftpSshKeyDialogOpen} 
        onClose={() => {
          setViewSftpSshKeyDialogOpen(false);
          setShowSftpSshKey(false);
          if (sftpSshKeyViewTimeout) {
            clearTimeout(sftpSshKeyViewTimeout);
            setSftpSshKeyViewTimeout(null);
          }
        }} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>SFTP SSH Key Details</DialogTitle>
        <DialogContent>
          {viewingSftpSshKey && (
            <Box>
              <Typography variant="h6" gutterBottom>{viewingSftpSshKey.name}</Typography>
              <Box mb={2}>
                <Chip
                  label={viewingSftpSshKey.status}
                  color={getStatusColor(viewingSftpSshKey.status) as any}
                  sx={{ mr: 1 }}
                />
                {viewingSftpSshKey.expiresAt && (
                  <Chip
                    label={`Expires: ${new Date(viewingSftpSshKey.expiresAt).toLocaleDateString()}`}
                    variant="outlined"
                  />
                )}
              </Box>
              
              <Typography variant="subtitle2" gutterBottom>Connection Details:</Typography>
              <Box mb={2}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Username:</strong> {viewingSftpSshKey.username}<br />
                  <strong>Server:</strong> {viewingSftpSshKey.sftpServer}:{viewingSftpSshKey.sftpPort}
                </Typography>
              </Box>

              <Typography variant="subtitle2" gutterBottom>Private Key:</Typography>
              {showSftpSshKey && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="caption">
                    This private key will be automatically hidden in 5 minutes for security.
                  </Typography>
                </Alert>
              )}
              <Box display="flex" alignItems="center" mb={2}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  value={showSftpSshKey ? viewingSftpSshKey.privateKey : '••••••••••••••••••••••••••••••••'}
                  variant="outlined"
                  size="small"
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Download Private Key">
                          <IconButton
                            onClick={() => handleDownloadSftpSshKey(viewingSftpSshKey)}
                            edge="end"
                            disabled={!showSftpSshKey}
                          >
                            <DownloadIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={showSftpSshKey ? "Hide Key" : "Show Key"}>
                          <IconButton
                            onClick={handleToggleSftpSshKeyVisibility}
                            edge="end"
                          >
                            {showSftpSshKey ? <HideIcon /> : <ViewIcon />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Copy to Clipboard">
                          <IconButton
                            onClick={() => handleCopySftpSshKey(viewingSftpSshKey.privateKey)}
                            edge="end"
                            disabled={!showSftpSshKey}
                          >
                            <CopyIcon />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Typography variant="subtitle2" gutterBottom>Connection Command:</Typography>
              <Box display="flex" alignItems="center" mb={2}>
                <TextField
                  fullWidth
                  value={`sftp -i private_key_file ${viewingSftpSshKey.username}@${viewingSftpSshKey.sftpServer}`}
                  variant="outlined"
                  size="small"
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => handleCopySftpSshKey(`sftp -i private_key_file ${viewingSftpSshKey.username}@${viewingSftpSshKey.sftpServer}`)}
                          edge="end"
                        >
                          <CopyIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {viewingSftpSshKey.lastUsed && (
                <>
                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>Last Used:</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {new Date(viewingSftpSshKey.lastUsed).toLocaleString()}
                  </Typography>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewSftpSshKeyDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Download Format Selection Dialog */}
      <Dialog 
        open={downloadFormatDialogOpen} 
        onClose={() => {
          setDownloadFormatDialogOpen(false);
          setPendingDownloadKey(null);
        }} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>Select Download Format</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Choose the format for your private key download. The backend generates PKCS#8 format, but you can convert to other formats:
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Card 
                variant="outlined" 
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: 'action.hover' }
                }}
                onClick={() => handleDownloadKeyInFormat('pkcs8')}
              >
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    PKCS#8 Format
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Standard format used by most modern SSH clients and SDKs. 
                    Compatible with OpenSSH, PuTTY, and other SSH tools.
                  </Typography>
                  <Typography variant="caption" color="primary" sx={{ mt: 1, display: 'block' }}>
                    Recommended for SDK-based access and modern SSH clients
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12}>
              <Card 
                variant="outlined" 
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: 'action.hover' }
                }}
                onClick={() => handleDownloadKeyInFormat('openssh')}
              >
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    OpenSSH Format
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Modern OpenSSH format that starts with "-----BEGIN OPENSSH PRIVATE KEY-----".
                    Best compatibility with FileZilla, WinSCP, and other modern SFTP clients.
                  </Typography>
                  <Typography variant="caption" color="primary" sx={{ mt: 1, display: 'block' }}>
                    Recommended for FileZilla and modern SFTP clients
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12}>
              <Card 
                variant="outlined" 
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: 'action.hover' }
                }}
                onClick={() => handleDownloadKeyInFormat('pkcs1')}
              >
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    PKCS#1 Format
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Traditional RSA format used by older SSH clients and some legacy systems.
                    May be required for compatibility with certain SFTP clients.
                  </Typography>
                  <Typography variant="caption" color="primary" sx={{ mt: 1, display: 'block' }}>
                    Use for legacy clients or when PKCS#8 is not supported
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setDownloadFormatDialogOpen(false);
              setPendingDownloadKey(null);
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notifications */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert onClose={() => setError(null)} severity="error">
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage(null)}
      >
        <Alert onClose={() => setSuccessMessage(null)} severity="success">
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PartnerIntegration; 